import React, { useMemo, useRef, useState } from 'react';
import { Box, Button, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ImageIcon from '@mui/icons-material/Image';
import html2canvas from 'html2canvas';
import { useI18n } from '../i18n';

const RECEIPT_LANGS = [
  { code: 'ru', label: 'РУС' },
  { code: 'tm', label: 'TKM' },
  { code: 'en', label: 'ENG' },
  { code: 'tr', label: 'TUR' },
];

const RECEIPT_LANG_CODES = RECEIPT_LANGS.map((item) => item.code);

const RECEIPT_TRANSLATIONS = {
  ru: {
    title: 'ЧЕК ОБ ОПЛАТЕ',
    orderNumber: 'Номер заказа',
    paymentDate: 'Дата и время оплаты',
    service: 'Сервис',
    topupNumber: 'Номер пополнения',
    topupAmount: 'Сумма пополнения',
    total: 'ИТОГО',
    thanks: 'Спасибо за оплату!',
  },
  tm: {
    title: 'TÖLEG ÇEKI',
    orderNumber: 'Sargyt belgisi',
    paymentDate: 'Töleg senesi we wagty',
    service: 'Hyzmat',
    topupNumber: 'Doldurmak belgisi',
    topupAmount: 'Doldurmak mukdary',
    total: 'JEMI',
    thanks: 'Tölegiňiz üçin sag boluň!',
  },
  en: {
    title: 'PAYMENT RECEIPT',
    orderNumber: 'Order number',
    paymentDate: 'Payment date and time',
    service: 'Service',
    topupNumber: 'Top-up number',
    topupAmount: 'Top-up amount',
    total: 'TOTAL',
    thanks: 'Thank you for your payment!',
  },
  tr: {
    title: 'ÖDEME MAKBUZU',
    orderNumber: 'Sipariş numarası',
    paymentDate: 'Ödeme tarihi ve saati',
    service: 'Hizmet',
    topupNumber: 'Yükleme numarası',
    topupAmount: 'Yükleme tutarı',
    total: 'TOPLAM',
    thanks: 'Ödemeniz için teşekkür ederiz!',
  },
};

function pickInitialLang(orderLang, uiLang) {
  const candidates = [orderLang, uiLang, 'ru'];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const lower = String(candidate).toLowerCase();
    if (RECEIPT_LANG_CODES.includes(lower)) return lower;
  }
  return 'ru';
}

function formatDateTime(value) {
  if (!value) return '-';
  const d = new Date(String(value).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatPhoneDist(value) {
  if (value === null || value === undefined || value === '') return '-';
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return '-';
  const local = digits.startsWith('993') ? digits.slice(3) : digits;
  return `+993 ${local}`;
}

function formatAmount(value) {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ReceiptRow({ label, value }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        mb: '8px',
      }}
    >
      <Box sx={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </Box>
      <Box sx={{ fontSize: '14px', color: '#000', fontWeight: 700, wordBreak: 'break-word' }}>
        {value || '-'}
      </Box>
    </Box>
  );
}

export default function OrderReceipt({ order }) {
  const { t, lang: uiLang } = useI18n();
  const receiptRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [receiptLang, setReceiptLang] = useState(() => pickInitialLang(order?.lang, uiLang));

  const tr = useMemo(() => RECEIPT_TRANSLATIONS[receiptLang] || RECEIPT_TRANSLATIONS.ru, [receiptLang]);

  if (!order) return null;

  const paidAmount = `${formatAmount(order.amount_src)} ${order.currency_src || ''}`.trim();
  const distAmount = order.amount_dist ? `${formatAmount(order.amount_dist)} ${order.currency_dist || ''}`.trim() : '';

  const handleLangChange = (_event, next) => {
    if (!next) return;
    setReceiptLang(next);
  };

  const handlePrint = () => {
    if (!receiptRef.current) return;
    const html = receiptRef.current.outerHTML;
    const w = window.open('', '_blank', 'width=420,height=720');
    if (!w) return;
    w.document.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${tr.title} ${order.external_order_id || ''}</title>
<style>
  @page { margin: 8mm; }
  html, body { margin: 0; padding: 0; background: #fff; font-family: 'Courier New', Consolas, monospace; }
  body { display: flex; justify-content: center; padding: 16px; }
</style>
</head>
<body>${html}
<script>
  window.addEventListener('load', function() {
    setTimeout(function() { window.print(); window.close(); }, 200);
  });
</script>
</body>
</html>`);
    w.document.close();
  };

  const handleSavePng = async () => {
    if (!receiptRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(receiptRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt_${order.external_order_id || order.id || 'order'}_${receiptLang}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={receiptLang}
          onChange={handleLangChange}
          aria-label="receipt language"
        >
          {RECEIPT_LANGS.map((item) => (
            <ToggleButton key={item.code} value={item.code} sx={{ px: 1.5, fontWeight: 700 }}>
              {item.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box
        ref={receiptRef}
        sx={{
          mx: 'auto',
          width: '320px',
          maxWidth: '100%',
          bgcolor: '#fff',
          color: '#000',
          fontFamily: '"Courier New", Consolas, monospace',
          p: '20px 18px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          position: 'relative',
          '&::before, &::after': {
            content: '""',
            display: 'block',
            position: 'absolute',
            left: 0,
            right: 0,
            height: '10px',
            background:
              'linear-gradient(135deg, #fff 25%, transparent 25%) 0 0/12px 12px, linear-gradient(225deg, #fff 25%, transparent 25%) 0 0/12px 12px',
            backgroundColor: '#fafafa',
          },
          '&::before': { top: '-10px' },
          '&::after': { bottom: '-10px', transform: 'scaleY(-1)' },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: '12px' }}>
          <img
            src="/terbium_logo.svg"
            alt="Terbium"
            crossOrigin="anonymous"
            style={{ height: '36px', width: 'auto' }}
          />
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            fontSize: '13px',
            fontWeight: 700,
            letterSpacing: '2px',
            mb: '8px',
          }}
        >
          {tr.title}
        </Box>

        <Box sx={{ borderTop: '1px dashed #000', my: '10px' }} />

        <ReceiptRow label={tr.orderNumber} value={order.external_order_id} />
        <ReceiptRow label={tr.paymentDate} value={formatDateTime(order.payment_time || order.created_at)} />
        <ReceiptRow label={tr.service} value={order.service_name} />
        <ReceiptRow label={tr.topupNumber} value={formatPhoneDist(order.phone_dist)} />

        <Box sx={{ borderTop: '1px dashed #000', my: '10px' }} />

        {distAmount && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', mb: '4px' }}>
            <span>{tr.topupAmount}</span>
            <span style={{ fontWeight: 700 }}>{distAmount}</span>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '16px',
            fontWeight: 800,
            mt: '6px',
          }}
        >
          <span>{tr.total}</span>
          <span>{paidAmount}</span>
        </Box>

        <Box sx={{ borderTop: '1px dashed #000', my: '10px' }} />

        <Box sx={{ textAlign: 'center', fontSize: '11px', color: '#333', lineHeight: 1.5 }}>
          {tr.thanks}
          <br />
          terbium.pro
        </Box>
      </Box>

      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          {t('Печать')}
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<ImageIcon />}
          onClick={handleSavePng}
          disabled={busy}
        >
          {busy ? t('Сохранение...') : t('Сохранить PNG')}
        </Button>
      </Stack>
    </Box>
  );
}
